import { defineComponent as _defineComponent } from 'vue'
import BaseDialog from "@/components/base-dialog.vue";
import { OnlineAnswersheetAttachment } from "@/composables/online-answersheet/use-online-answersheet-attachments";
import useHttpRequest from "@/composables/use-http-request";
import { useShowWarningMessage } from "@/composables/use-notifications";
import { useUploadResourceToAWS } from "@/composables/use-upload-resource-to-aws";
import AwsUploadUrl from "@/interfaces/awsUploadUrl.interface";
import CourseWork from "@/interfaces/courseWork.interface";
import { Loading } from "element-ui";
import { ref } from "vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'add-course-work-attachment-dialog',
  props: {
    isVisible: { type: Boolean },
    onClose: null,
    courseWork: null
  },
  emits: {
  added: (attachment: OnlineAnswersheetAttachment) => !!attachment,
},
  setup(__props: any, { emit }) {

const props = __props as {
  isVisible: boolean;
  onClose: () => void;
  courseWork: CourseWork | null;
}

const title = ref("");
const selectedFile = ref<File | null>(null);





const closeDialog = () => {
  title.value = "";
  selectedFile.value = null;
  props.onClose();
};

const showTitleError = () => {
  return useShowWarningMessage(
    "Title Required",
    "Please enter a title for the attachment you're trying to add"
  );
};

const onAttachmentUploaded = async ({
  file_path,
  uuid,
}: {
  uuid: string;
  file_path: string;
}) => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
  }>({
    method: "PATCH",
    url: "course-work/attachments/on-uploaded",
    body: {
      uuid,
      file_path,
      student_course_work_submission_id:
        props.courseWork?.student_course_work_submissions?.[0]
          ?.student_course_work_submission_id,
    },
    loadingProperty: null,
    showSuccessMessage: false,
    errorLoadingProperty: null,
    showFullScreenLoader: true,
  });

  if (!request) {
    return;
  }

  if (
    request.success &&
    request.message == "Attachment data updated successfully"
  ) {
    emit("added", {
      title: title.value.trim(),
      file_path,
      file_size: selectedFile.value?.size ?? 0,
      id: uuid,
    });

    return closeDialog();
  } else {
    useShowWarningMessage("Error", "An error occurred while uploading files");
  }
};

const uploadAttachment = async () => {
  let loadingIndicator: ReturnType<typeof Loading.service> | undefined;

  try {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      upload_url: AwsUploadUrl;
      uuid: string;
    }>({
      method: "POST",
      url: "course-work/attachments/request-for-upload-url",
      body: {
        title: title.value.trim(),
        file_name: selectedFile.value?.name,
        file_size: selectedFile.value?.size,
        student_course_work_submission_id:
          props.courseWork?.student_course_work_submissions?.[0]
            ?.student_course_work_submission_id,
      },
      loadingProperty: null,
      showSuccessMessage: false,
      errorLoadingProperty: null,
      showFullScreenLoader: true,
    });

    if (!request) {
      return;
    }

    if (request.success && request.message == "Upload url generated") {
      loadingIndicator = Loading.service({
        lock: true,
        text: "Please wait...",
        background: "rgba(0, 0, 0, 0.7)",
      });

      const uploadedFileUrl = await useUploadResourceToAWS({
        awsUploadUrl: request.upload_url,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedFile: selectedFile.value!,
        onProgress: (progressEvent: ProgressEvent) => {
          const loader = document.querySelector(
            ".el-loading-text"
          ) as HTMLElement;

          const progress = +(
            (progressEvent.loaded / progressEvent.total) *
            100
          ).toFixed(0);

          loader.innerText = `Adding attachment... ${progress}%`;
        },
      });

      if (!uploadedFileUrl) {
        return;
      }

      return onAttachmentUploaded({
        file_path: uploadedFileUrl,
        uuid: request.uuid,
      });
    }
  } catch (error) {
    useShowWarningMessage("Error", "An error occurred while uploading files");
  } finally {
    loadingIndicator?.close();
  }
};

const onSelectFile = (e: any) => {
  e.preventDefault();

  if (!e.target.files?.length) {
    return;
  }

  selectedFile.value = e.target.files[0];

  //Reset the input value to allow re-uploading the same file
  e.target.value = null;
  return uploadAttachment();
};

return { __sfc: true,title, selectedFile, props, emit, closeDialog, showTitleError, onAttachmentUploaded, uploadAttachment, onSelectFile, BaseDialog }
}

})